import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { trackingConfig } from '@config/tracking.config';
import { BreakpointsService } from '@core/services/breakpoints.service';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { HelpFormRoutingEnum } from '@features/help-form/help-form-routing.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterState } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { JwtState } from '@stores/jwt/jwt.state';
import { SessionState } from '@stores/session/session.state';
import { WINDOW } from '@wizbii/angular-utilities';
import { TimeOneService } from '@wizbii/seo-lib';
import { createTrackingConfig, setupTracking, trackPageView } from '@wizbii/tracking';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, combineLatest, from, fromEvent, Observable } from 'rxjs';
import { delay, distinctUntilChanged, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-core',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, AfterViewInit {
  showInterserviceHeader$!: Observable<boolean>;
  apiDomain = environment.api.domain;

  /**
   * Use class `hover-on` in CSS as follows:
   * `:host-context(.hover-on) .link:hover { ... }`
   */
  @HostBinding('class.hover-on') hoverEnabled = true;

  @HostBinding('class.hover-off')
  get hoverDisabled() {
    return !this.hoverEnabled;
  }

  /**
   * Class `focus-off` disables all outlines automatically.
   */
  @HostBinding('class.focus-off') focusOutlineDisabled = false;

  get applicationId(): string {
    return environment.applicationId;
  }

  trackingIsSetup$ = new BehaviorSubject<boolean>(false);
  isPlayerRoute$!: Observable<boolean>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly renderer: Renderer2,
    private readonly breakpointsService: BreakpointsService,
    private readonly gtmService: GoogleTagManagerService,
    private readonly cookieService: CookieService,
    private readonly timeoneService: TimeOneService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(WINDOW) private readonly window: any
  ) {}

  ngOnInit(): void {
    if (this.document.documentElement.lang === '') {
      this.document.documentElement.lang = environment.i18n.lang;
    }

    this.showInterserviceHeader$ = this.store.select(RouterState.state).pipe(
      filter((state) => !!state),
      map((state: any) =>
        typeof state.data?.showInterserviceHeader === 'boolean' ? state.data?.showInterserviceHeader : true
      ),
      distinctUntilChanged()
    );

    if (isPlatformServer(this.platformId)) {
      return;
    }

    /**
     * Disable hover on `touchstart` to cover browsers that do not support pointer events.
     * https://caniuse.com/#feat=pointer
     */
    fromEvent(this.window, 'touchstart', { passive: true }).subscribe(() => {
      this.hoverEnabled = false;
    });

    this.isPlayerRoute$ = this.store.select(RouterState).pipe(
      filter((router) => !!router),
      map((router) => router.state?.url === `/${FeaturesRoutingEnum.HelpForm}/${HelpFormRoutingEnum.Player}`),
      distinctUntilChanged()
    );

    this.initTracking();
    this.initTimeOne();
  }

  ngAfterViewInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    combineLatest([this.router.events, this.trackingIsSetup$])
      .pipe(filter(([event, trackingIsSetup]) => event instanceof NavigationEnd && !!trackingIsSetup))
      .subscribe(() => {
        trackPageView();
        this.initTimeOnePageView();
      });
  }

  initTimeOne() {
    if (this.cookieService.check('timeone_session')) {
      this.timeoneService.initGlobals();
      return;
    }

    this.route.queryParamMap
      .pipe(
        untilDestroyed(this),
        map((params) => params.get('utm_source')),
        filter((utmSource) => !!utmSource && utmSource === 'timeone'),
        map((utmSource) => utmSource === 'timeone'),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      )
      .subscribe((isUtmSourceTimeOne) => this.initTimeOneGlobals(isUtmSourceTimeOne));
  }

  initTimeOneGlobals(isUtmSourceTimeOne: boolean) {
    if (isUtmSourceTimeOne) {
      if (!this.cookieService.check('timeone_session')) {
        const cookieTimeoneDate = new Date();
        cookieTimeoneDate.setDate(cookieTimeoneDate.getDate() + 7);

        this.cookieService.set('timeone_session', 'true', cookieTimeoneDate, '/', environment.cookieMoneyDomain);
      }

      this.timeoneService.initGlobals();
    }
  }

  private initTracking(): void {
    const initTracking$ = this.store.select(SessionState.isInitialized).pipe(
      filter((isInitialized) => !!isInitialized),
      switchMap(() =>
        combineLatest([this.store.selectOnce(JwtState), this.store.selectOnce(SessionState.identityCard)])
      ),
      first(),
      map(([tokens, identityCard]) =>
        createTrackingConfig(trackingConfig, {
          ...(!!tokens ? { token: tokens.token } : {}),
          ...(!!identityCard ? { firstName: identityCard.firstName } : {}),
          ...(!!identityCard ? { lastName: identityCard.lastName } : {}),
        })
      ),
      // tslint:disable-next-line: deprecation
      switchMap((config) => from(setupTracking(config))),
      switchMap(() => this.trackingIsSetup$),
      tap((trackingIsSetup) => {
        if (!trackingIsSetup) {
          trackPageView();

          this.initTimeOnePageView();

          this.trackingIsSetup$.next(true);
        }
      })
    );

    combineLatest([
      initTracking$,
      this.isPlayerRoute$.pipe(delay(500)),
      this.breakpointsService.isMobile$.pipe(filter((isMobile) => !!isMobile)),
    ]).subscribe(([isTrackerInit, isPlayerRoute]) => {
      const bannerElement = this.document.querySelector('[class^="gdpr_banner"]');
      if (!bannerElement) {
        return;
      }
      if (isPlayerRoute) {
        this.renderer.addClass(bannerElement, 'gdpr_banner--top');
        return;
      }
      this.renderer.removeClass(bannerElement, 'gdpr_banner--top');
    });

    this.gtmService.addGtmToDom();
  }

  initTimeOnePageView() {
    if (this.cookieService.check('timeone_session')) {
      this.timeoneService.addDot({ event: 'pageview' });
    }
  }

  /**
   * Enable hover if "mouse" pointer event is detected; disable it otherwise.
   * https://developer.mozilla.org/en-US/docs/Web/Events/pointerenter
   */
  @HostListener('pointerenter', ['$event'])
  onPointerEnter(event: any) {
    const evt: PointerEvent = event; // https://github.com/kulshekhar/ts-jest/issues/1035
    this.hoverEnabled = evt.pointerType === 'mouse';
  }

  @HostListener('mousedown')
  onMouseDown() {
    this.focusOutlineDisabled = true;
  }

  @HostListener('keydown.Tab')
  onTabKeyDown() {
    this.focusOutlineDisabled = false;
  }
}
